/**
 * Publication Bulk Edit Renderer
 *
 * The bulk-edit run has been emitted server-side since it shipped
 * (`PublicationBulkEditBatchProcessStarted` -> operation name `PublicationBulkEdit`),
 * but no renderer was ever registered for it, so it fell through to
 * `DefaultRenderer` — a grey question mark labelled "Operación desconocida" over a
 * raw JSON dump. This is the missing registration.
 *
 * Data shape (from PublicationBulkEditBatchProcessStartedEventSchema, minus
 * `totalItems`, which the projector lifts onto the row):
 *   changes: Array<{ publicationId, fields }>
 */

import { FC } from 'react';
import { Stack, Text } from '@mantine/core';
import type { DetailRendererProps, OperationRendererConfig, ProcessData } from './index';

type BulkEditChange = {
  publicationId: string;
  fields?: Record<string, unknown>;
};

const changesOf = ( data: ProcessData | undefined ): BulkEditChange[] =>
  ( data?.changes || []) as BulkEditChange[];

/**
 * The distinct field NAMES touched across the run — the one fact that makes a bulk
 * edit legible at a glance ("price and stock on 42 publications"). Values are
 * deliberately not shown: they differ per publication, so any single one would be
 * a lie about the rest.
 */
const editedFieldNames = ( changes: BulkEditChange[]): string[] =>
  [...new Set( changes.flatMap(( c ) => Object.keys( c.fields ?? {})))];

const DetailComponent: FC<DetailRendererProps> = ({ process, t }) => {
  const changes = changesOf( process.data as ProcessData );
  const fields = editedFieldNames( changes );

  return (
    <Stack gap="sm">
      <Text fz="sm" c="dimmed">
        {t( 'batchProcesses_operations_PublicationBulkEdit_description' )}
      </Text>

      {fields.length > 0 && (
        <Stack gap="xs">
          <Text fz="sm" fw={500}>
            {t( 'batchProcesses_renderer_editedFields' )}:
          </Text>
          <Text fz="sm">{fields.join( ', ' )}</Text>
        </Stack>
      )}
    </Stack>
  );
};

export const config: Omit<OperationRendererConfig, 'icon' | 'color' | 'labelKey'> = {
  getSummary: ( data, t, process ) => {
    // `changes` is the authoritative count, but a row whose `data` was trimmed still
    // has the projector's `totalItems` — prefer the former, fall back to the latter.
    const count = changesOf( data ).length || ( process as { totalItems?: number })?.totalItems || 0;
    // Reuses the shared publication-count phrase (same key `PublicationCreationRenderer`
    // uses) rather than minting a near-duplicate: the summary line is "how many
    // publications", identical in both cases.
    return t( 'batchProcesses_summary_publications', { count });
  },
  DetailComponent,
  getRequiredReferenceIds: ( process ) => ({
    publicationIds: changesOf( process?.data as ProcessData ).map(( c ) => c.publicationId )
  })
};

export default DetailComponent;
